import classNames from "classnames";
import { TriageBucket } from "../sensorHealth.utility";

const FILL_CLASSES: Record<TriageBucket, string> = {
  working: "bg-emerald-500",
  "needs-look": "bg-amber-500",
  "not-responding": "bg-gray-300",
  unlinked: "bg-gray-400",
};

const clampPercent = (value: number) => Math.max(0, Math.min(100, value));

type DetectionGaugeProps = {
  /** Current live reading. */
  reading: number;
  /** Detection threshold, or null for an unlinked sensor (no detecting zone). */
  threshold: number | null;
  /** Shared scale ceiling — the reading and threshold are plotted against this, not the raw byte max. */
  scaleMax: number;
  bucket: TriageBucket;
  /** Fill in muted tones and drop the live emphasis when the board is frozen. */
  frozen?: boolean;
};

/**
 * Plots the reading on a data-driven 0–scaleMax range with the detection threshold marked and the
 * "detecting" zone shaded, so presence reads spatially rather than as a bare number. scaleMax is
 * shared with the trend chart so the threshold sits at the same height in both.
 */
export const DetectionGauge = ({ reading, threshold, scaleMax, bucket, frozen }: DetectionGaugeProps) => {
  const readingPercent = clampPercent((reading / scaleMax) * 100);
  const thresholdPercent = threshold == null ? null : clampPercent((threshold / scaleMax) * 100);

  return (
    <div>
      <div className="relative h-3 w-full rounded-full bg-gray-100">
        {thresholdPercent != null && (
          <div
            className="absolute inset-y-0 right-0 rounded-r-full bg-emerald-500/10"
            style={{ left: `${thresholdPercent}%` }}
          />
        )}
        <div
          className={classNames("absolute inset-y-0 left-0 rounded-full", frozen ? "bg-gray-300" : FILL_CLASSES[bucket])}
          style={{ width: `${readingPercent}%` }}
        />
        {thresholdPercent != null && (
          <div
            className="absolute -top-1 -bottom-1 w-0.5 -translate-x-1/2 rounded bg-gray-400"
            style={{ left: `${thresholdPercent}%` }}
          />
        )}
      </div>
      <div className="relative mt-1.5 h-4 font-mono text-[11px] tabular-nums text-gray-400">
        <span className="absolute left-0">0</span>
        {thresholdPercent != null && (
          <span
            className="absolute -translate-x-1/2 whitespace-nowrap text-gray-500"
            style={{ left: `${thresholdPercent}%` }}
          >
            {Math.round(threshold as number)}
          </span>
        )}
        <span className="absolute right-0">{Math.round(scaleMax)}</span>
      </div>
    </div>
  );
};
